import React, { useState } from 'react';
import proptypes from 'prop-types';
import Markdown from 'markdown-to-jsx';
import {
  Alert,
  Button,
  FormLabel,
  IconButton,
  Input,
  Skeleton,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService } from '@thd-nucleus/experience-context';
import { ThumbsUpThumbsDownWithPopover } from '@thd-olt-component-react/user-feedback';
import { Close } from '@one-thd/sui-icons';
import classnames from 'classnames';
import { useQuestionBot } from '../../hooks/useQuestionBot';
import { getSessionId } from '../../utils/session-utils';
import { Spinner } from '../../assets/Spinner';
// eslint-disable-next-line @harmony/stencil/no-import-css
import './BotPrompt.scss';

const { bool, string } = proptypes;

const USER_QUESTION_CHARACTER_LIMIT = 255;
const keyPressMap = { Enter: false, Shift: false };

export const BotPrompt = ({ itemId, force }) => {
  const [userQuestion, setUserQuestion] = useState('');
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const { error, loading, response } = useQuestionBot(submittedQuestion, itemId);

  const magicApronBotEnabled = useConfigService('fs-prop:magic-apron-qa-bot-enabled');

  if (!magicApronBotEnabled && !force) {
    return null;
  }

  const onUserQuestionSubmit = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-bot.submit-click', {
      sessionID: getSessionId(),
      question: userQuestion,
    });
    setSubmittedQuestion(userQuestion);
  };
  const onUserQuestionChange = (event) => {
    if (!(keyPressMap.Enter)) {
      setUserQuestion(event.target.value);
    }
  };

  const onUserQuestionKeyPress = (event) => {
    // Keeps track of all currently pressed keys
    keyPressMap[event.key] = event.type === 'keydown';
    if (keyPressMap.Enter) {
      onUserQuestionSubmit();
    }
  };

  const clearPrompt = () => {
    setUserQuestion('');
    setSubmittedQuestion('');
  };

  const handleFeedbackData = () => {
    return {
      questionAndAnswer: {
        question: userQuestion,
        answer: response
      },
      reviewSummary: ''
    };
  };

  if (!userQuestion && submittedQuestion) {
    clearPrompt();
  }

  const containerClasses = classnames('sui-p-4 sm:sui-p-8 sui-flex sui-flex-col sm:sui-flex-row '
    + 'sui-gap-2 sm:sui-gap-3 sui-bg-primary sui-w-full sui-mb-4 sui-border-1 sui-border-primary sui-border-solid');

  return (
    <div
      data-component="magic-apron-bot-prompt"
      className={containerClasses}
    >
      <div className="sui-flex sui-items-center">
        <div className="sui-mr-4">
          <div className="sui-flex sui-items-center sui-justify-center sui-w-14 sui-h-14">
            <Spinner spinning={!!loading} />
          </div>
        </div>
        <div className="sui-flex sui-flex-col sui-min-w-32">
          <FormLabel htmlFor="qa-bot-input">
            <Typography variant="h3">Ask about this product</Typography>
          </FormLabel>
          <Typography variant="body-base">Get an answer now with AI</Typography>
        </div>
      </div>
      <div className="sui-flex sui-flex-col sui-flex-grow sui-p-4 sui-bg-subtle">
        <div className="sui-flex sui-flex-col sm:sui-flex-row">
          <div className="sui-mb-2 sm:sui-grow sm:sui-mr-4">
            <Input
              id="qa-bot-input"
              value={userQuestion}
              onChange={onUserQuestionChange}
              onKeyDown={onUserQuestionKeyPress}
              onKeyUp={onUserQuestionKeyPress}
              fullWidth
              multiline
              placeholder="Type a question"
              inputAttributes={{
                maxLength: USER_QUESTION_CHARACTER_LIMIT,
                resize: 'none',
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: 'false'
              }}
              endAdornment={!!userQuestion.length && (
                <IconButton
                  icon={Close}
                  size="small"
                  onClick={clearPrompt}
                  aria-label="Clear question"
                />
              )}
            />
          </div>
          <div className="sui-mb-4">
            <Button
              variant="secondary"
              onClick={onUserQuestionSubmit}
              id="magic_apron_qa_find_it_for_me"
              disabled={loading}
            >
              Get an Answer
            </Button>
          </div>
        </div>
        {/* eslint-disable-next-line @harmony/stencil/no-tailwind-disable */}
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="sui-flex sui-flex-col sui-mb-2 sui-max-w-[800px]">
          <BotResponse loading={loading} error={error} response={response} />
        </div>
        {/* eslint-disable-next-line @harmony/stencil/no-tailwind-disable */}
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="sui-max-w-[800px]">
          <Typography variant="body-xs" color="subtle" height="tight">
            {/* eslint-disable-next-line max-len */}
            AI-generated from the text of manufacturer documentation. To verify or get additional information, please contact The Home Depot customer service.
          </Typography>
        </div>
        {response && !loading && (
          <div className="sui-mt-4">
            <ThumbsUpThumbsDownWithPopover
              anchorKey={itemId}
              anchorType="PIP"
              customerInsights="QABot"
              handleFeedbackData={handleFeedbackData}
            />
          </div>
        )}
      </div>
    </div>
  );
};

BotPrompt.displayName = 'BotPrompt';
BotPrompt.propTypes = {
  itemId: string,
  force: bool
};

BotPrompt.defaultProps = {
  itemId: '',
  force: false
};

const BotResponse = ({ loading, error, response }) => {
  if (loading) {
    return (
      <Skeleton grow>
        <SkeletonLine variant="multi" numberOfLines={3} />
      </Skeleton>
    );
  }

  if (error) {
    return (
      <div className="sui-mt-3">
        <Alert status="error" headline="Something went wrong on our end">
          Please refresh the page or try again later.
        </Alert>
      </div>
    );
  }

  if (response) {
    return (
      <Typography variant="body-base" height="tight" component="div">
        <div className="bot-prompt">
          <div className="sui-mb-2">
            <Typography variant="h6">Answer</Typography>
          </div>
          <Markdown>{response.replaceAll(/\n\n/g, '\n\n &nbsp; \n\n')}</Markdown>
        </div>
      </Typography>
    );
  }

  return null;
};

BotResponse.propTypes = {
  loading: bool,
  error: bool,
  response: string
};

BotResponse.defaultProps = {
  loading: false,
  error: false,
  response: ''
};

BotPrompt.dataModel = extend(
  ThumbsUpThumbsDownWithPopover
);
